import { Checkbox } from '@ardoq/forms';
import { type I18nLocale, t } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';
import { Box, Stack } from '@ardoq/layout';
import { Skeleton, SkeletonLoader } from '@ardoq/loader';
import { ModalLayout, ModalSize, ModalTemplate } from '@ardoq/modal';
import { Select } from '@ardoq/select';
import { Paragraph } from '@ardoq/typography';
import { useRef, useState } from 'react';
import type {
  BulkEditFieldColumn,
  BulkEditFieldData,
  BulkEditFieldValueSelection,
} from '../../types';
import {
  type BulkEditFieldFormState,
  bulkEditFieldOperations,
} from './bulkEditFieldOperations';
import {
  type GetBulkEditAffectedComponentCount,
  useAffectedComponentCount,
} from './useAffectedComponentCount';
import { BulkEditValueModeSelector } from './BulkEditValueModeSelector';

export type BulkEditFieldDialogProps = {
  columns: BulkEditFieldColumn[];
  initialColumnKey: string;
  getBulkEditAffectedComponentCount: GetBulkEditAffectedComponentCount;
  i18nLocale: I18nLocale;
  locale: Locale;
  onCancel: VoidFunction;
  onSubmit: (update: BulkEditFieldData) => void;
};

const getDescriptionContent = (
  hasCountError: boolean,
  count: number | null,
  i18nLocale: I18nLocale
) => {
  // The error is already announced: ModalTemplate renders it in an
  // ErrorNotification, which carries role="alert".
  if (hasCountError) {
    return null;
  }
  if (count === null) {
    return (
      <SkeletonLoader
        message={t(
          i18nLocale,
          'inventory:bulkEditField.countLoading',
          'Counting the components this applies to'
        )}
      >
        <Skeleton width="80%" />
      </SkeletonLoader>
    );
  }
  return (
    <Paragraph variant="text2">
      {t(
        i18nLocale,
        'inventory:bulkEditField.description',
        { count },
        count === 1
          ? 'Applies to 1 component matching your current filters.'
          : `Applies to ${count} components matching your current filters.`
      )}
    </Paragraph>
  );
};

/**
 * The count arrives after the dialog opens and changes again with the field, so
 * the sentence is a status message (WCAG 4.1.3). The region is always mounted
 * and only its contents swap — a live region added to the DOM together with its
 * text is unreliably announced.
 */
const BulkEditFieldDescription = ({
  hasCountError,
  count,
  i18nLocale,
}: {
  hasCountError: boolean;
  count: number | null;
  i18nLocale: I18nLocale;
}) => (
  <Box role="status" aria-live="polite" aria-atomic="true">
    {getDescriptionContent(hasCountError, count, i18nLocale)}
  </Box>
);

const getSubmitButtonText = (count: number | null, i18nLocale: I18nLocale) => {
  if (count === null) {
    return t(
      i18nLocale,
      'inventory:bulkEditField.submitUnknownCount',
      'Update components'
    );
  }
  return t(
    i18nLocale,
    'inventory:bulkEditField.submit',
    { count },
    count === 1 ? 'Update one component' : `Update ${count} components`
  );
};

export const BulkEditFieldDialog = ({
  columns,
  initialColumnKey,
  getBulkEditAffectedComponentCount,
  i18nLocale,
  locale,
  onCancel,
  onSubmit,
}: BulkEditFieldDialogProps) => {
  // Resolved against the list rather than taken on trust: a key the list does
  // not hold would leave the form submitting one column while the inputs and
  // the count describe another.
  const [formState, setFormState] = useState<BulkEditFieldFormState>({
    columnKey: (
      columns.find(column => column.columnKey === initialColumnKey) ??
      columns[0]
    ).columnKey,
    selection: { mode: 'value', value: null },
    shouldOnlyFillEmptyCells: true,
  });

  const affectedComponentCount = useAffectedComponentCount(
    getBulkEditAffectedComponentCount,
    formState.columnKey
  );
  const count = bulkEditFieldOperations.getCount(
    affectedComponentCount,
    bulkEditFieldOperations.isOnlyFillingEmptyCells(formState)
  );
  const hasCountError = affectedComponentCount.status === 'error';
  const isClearing = formState.selection.mode === 'empty';
  const hasChangedFieldRef = useRef(false);

  const selectedColumn =
    columns.find(column => column.columnKey === formState.columnKey) ??
    columns[0];

  const selectColumn = (columnKey: string | null) => {
    const column = columns.find(it => it.columnKey === columnKey);
    if (!column) {
      return;
    }
    hasChangedFieldRef.current = true;
    setFormState(currentState => ({
      ...currentState,
      columnKey: column.columnKey,
      selection: {
        mode: 'value',
        value: bulkEditFieldOperations.getEmptyValue(column.inputType),
      },
    }));
  };

  const submit = () => {
    const submittedUpdate = bulkEditFieldOperations.toSubmittedUpdate(
      formState,
      selectedColumn,
      count
    );
    onSubmit(submittedUpdate);
  };

  const selectMode = (mode: BulkEditFieldValueSelection['mode']) =>
    setFormState(currentState => ({
      ...currentState,
      selection:
        mode === 'value'
          ? {
              mode,
              value: bulkEditFieldOperations.getEmptyValue(
                selectedColumn.inputType
              ),
            }
          : { mode },
    }));

  return (
    <ModalTemplate
      modalSize={ModalSize.M}
      headerText={t(
        i18nLocale,
        'inventory:bulkEditField.title',
        'Edit field in bulk'
      )}
      onCloseButtonClick={onCancel}
      closeButtonLabel={t(i18nLocale, 'inventory:bulkEditField.close', 'Close')}
      secondaryButtonText={t(
        i18nLocale,
        'inventory:confirmDialogs.cancel',
        'Cancel'
      )}
      onSecondaryButtonClick={onCancel}
      primaryButtonText={getSubmitButtonText(count, i18nLocale)}
      isPrimaryButtonDisabled={
        !bulkEditFieldOperations.isSubmittable(
          formState,
          affectedComponentCount
        )
      }
      onPrimaryButtonClick={submit}
      errorBlockMessage={
        hasCountError
          ? t(
              i18nLocale,
              'inventory:bulkEditField.countError',
              'We couldn’t count the components this would change. Close the dialog and try again.'
            )
          : undefined
      }
    >
      <ModalLayout>
        <Stack gap="medium">
          <BulkEditFieldDescription
            hasCountError={hasCountError}
            count={count}
            i18nLocale={i18nLocale}
          />
          <Select
            label={t(i18nLocale, 'inventory:bulkEditField.fieldLabel', 'Field')}
            popoverHelpContent={t(
              i18nLocale,
              'inventory:bulkEditField.fieldHelp',
              'The fields you can edit in this view. Calculated and read-only fields are left out.'
            )}
            i18nLocale={i18nLocale}
            options={columns.map(({ columnKey, label }) => ({
              label,
              value: columnKey,
            }))}
            value={selectedColumn.columnKey}
            onValueChange={selectColumn}
          />
          <BulkEditValueModeSelector
            column={selectedColumn}
            selection={formState.selection}
            shouldAutoFocusValueInput={!hasChangedFieldRef.current}
            i18nLocale={i18nLocale}
            locale={locale}
            onModeChange={selectMode}
            onValueChange={value =>
              setFormState(currentState => ({
                ...currentState,
                selection: { mode: 'value', value },
              }))
            }
          />
          <Checkbox
            // Clearing a field and only filling empty cells cancel out. The
            // handler has to turn the change away itself: `isDisabled` only
            // greys the wrapper, it never reaches the input.
            isDisabled={isClearing}
            isChecked={bulkEditFieldOperations.isOnlyFillingEmptyCells(
              formState
            )}
            onChange={shouldOnlyFillEmptyCells =>
              !isClearing &&
              setFormState(currentState => ({
                ...currentState,
                shouldOnlyFillEmptyCells,
              }))
            }
          >
            {t(
              i18nLocale,
              'inventory:bulkEditField.onlyFillEmptyCells',
              'Only fill empty cells'
            )}
          </Checkbox>
        </Stack>
      </ModalLayout>
    </ModalTemplate>
  );
};
